/**
 * Converts a non-negative integer (VND amount) to Vietnamese spoken form.
 * Caller appends " đồng" as needed.
 *
 * Examples:
 *   250000  → "hai trăm năm mươi nghìn"
 *   1500000 → "một triệu năm trăm nghìn"
 *   25000000 → "hai mươi lăm triệu"
 *   0 → "không"
 */

const ONES = ['', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín']

const TENS = [
    '',
    'mười',
    'hai mươi',
    'ba mươi',
    'bốn mươi',
    'năm mươi',
    'sáu mươi',
    'bảy mươi',
    'tám mươi',
    'chín mươi',
]

/**
 * Converts a number 1–999 to Vietnamese spoken form.
 * @param n integer 1–999
 * @param isLeadingGroup when false (not the highest group), zeros in hundreds position use "lẻ"
 */
function threeDigits(n: number, isLeadingGroup: boolean): string {
    const hundreds = Math.floor(n / 100)
    const remainder = n % 100
    const tens = Math.floor(remainder / 10)
    const ones = remainder % 10

    const parts: string[] = []

    if (hundreds > 0) {
        parts.push(`${ONES[hundreds]} trăm`)
    } else if (!isLeadingGroup && (tens > 0 || ones > 0)) {
        parts.push('không trăm')
    }

    if (tens === 0 && ones === 0) {
        // nothing
    } else if (tens === 0) {
        if (hundreds > 0 || !isLeadingGroup) {
            parts.push('lẻ')
        }
        parts.push(ONES[ones])
    } else if (tens === 1) {
        // "mười …"
        parts.push('mười')
        if (ones === 5) {
            parts.push('lăm')
        } else if (ones > 0) {
            parts.push(ONES[ones])
        }
    } else {
        parts.push(TENS[tens])
        if (ones === 5) {
            parts.push('lăm')
        } else if (ones > 0) {
            parts.push(ONES[ones])
        }
    }

    return parts.join(' ')
}

/**
 * Format a non-negative integer amount (VND) to Vietnamese spoken form.
 * Does NOT append " đồng" — caller is responsible.
 */
export function formatMoneySpeech(amount: number): string {
    const n = Math.floor(Math.abs(amount))

    if (n === 0) return 'không'

    const ty = Math.floor(n / 1_000_000_000)
    const trieuRem = n % 1_000_000_000
    const trieu = Math.floor(trieuRem / 1_000_000)
    const nghinRem = trieuRem % 1_000_000
    const nghin = Math.floor(nghinRem / 1_000)
    const donVi = nghinRem % 1_000

    const parts: string[] = []

    if (ty > 0) {
        parts.push(`${threeDigits(ty, true)} tỷ`)
    }
    if (trieu > 0) {
        parts.push(`${threeDigits(trieu, ty === 0)} triệu`)
    }
    if (nghin > 0) {
        parts.push(`${threeDigits(nghin, ty === 0 && trieu === 0)} nghìn`)
    }
    if (donVi > 0) {
        parts.push(threeDigits(donVi, ty === 0 && trieu === 0 && nghin === 0))
    }

    return parts.join(' ')
}
